import { Arrange, Columns, DatePicker, Tooltip } from "@fullscript/aviary-web";
import {
  faCheckCircle,
  faChevronRight,
  faHeart,
  faInfoCircle,
} from "@fullscript/pro-regular-svg-icons";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import {
  Typography,
  Spacer,
  Button,
  FloatingLabelInput,
  FloatingLabelSelect,
  PhoneInput,
  Message,
  useToast,
  Checkbox,
} from "@aviary";
import { getPhoneWithoutCountryCode, getMinPhoneLength } from "@aviary/components/PhoneInput";
import { isValidPhoneNumberPerCountry } from "@aviary/components/PhoneInput/validatePhoneNumber";
import { useBreakpoints } from "@shared/hooks/useBreakpoints/useBreakpoints";
import { FontAwesomeIcon } from "@shared/react-fontawesome/react-fontawesome";
import type {
  AuthPatientSignUpFromRxOptionalAttributes,
  LabsDirectCheckoutItemType,
} from "@shared/types/graphqlGenerated";
import { isDateOfBirthValid } from "@shared/utils/age/isDateOfBirthValid";
import { isMinor } from "@shared/utils/age/isMinor";
import { gRecaptchaExecute } from "@shared/utils/gRecaptchaExecute/gRecaptchaExecute";
import { PasswordInput } from "@unauthenticated/shared/components/PasswordInput/PasswordInput";
import { isPasswordValid } from "@unauthenticated/shared/components/PasswordInput/PasswordValidation/PasswordValidation";
import { usePatientSignUpUnauthenticatedSharedMutation } from "@unauthenticated/shared/data/mutations/PatientSignUp.mutation.generated";
import { l } from "@unauthenticated/signupSignin/locales/i18n";

interface PatientInfoState {
  email: string;
}

interface JourneyPatientInfoFormProps {
  onPatientCreated?: () => void;
  onSubmit?: () => void;
  email?: string;
  storeSlug?: string;
  storeId?: string;
  journeyPackageId?: string;
  labsCheckoutItemType?: LabsDirectCheckoutItemType;
  labsCheckoutItemId?: string;
  source?: string;
  patientInfoState?: PatientInfoState;
}

const JourneyPatientInfoForm = ({
  onPatientCreated,
  onSubmit,
  email: initialEmail,
  storeSlug,
  storeId,
  journeyPackageId,
  labsCheckoutItemType,
  labsCheckoutItemId,
  source = "journey_self_checkout",
  patientInfoState,
}: JourneyPatientInfoFormProps) => {
  const { t } = useTranslation();
  const { tablet } = useBreakpoints();
  const { makeToast } = useToast();

  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [dateOfBirth, setDateOfBirth] = useState<Date | undefined>(undefined);
  const [biologicalSex, setBiologicalSex] = useState("");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [isPregnantOrNursing, setIsPregnantOrNursing] = useState(false);

  const [password, setPassword] = useState("");
  const [passwordDirty, setPasswordDirty] = useState(false);
  const [recaptchaError, setRecaptchaError] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const isMobile = tablet.lessThan;
  const displayEmail = initialEmail || patientInfoState?.email || "";

  const dobISOString =
    dateOfBirth && isDateOfBirthValid(dateOfBirth) ? dateOfBirth.toISOString() : undefined;

  const redirectPath =
    storeSlug && journeyPackageId ? `/r/checkout/${storeSlug}/journey/${journeyPackageId}` : "";

  const hasLabsCheckoutAuth = Boolean(labsCheckoutItemType && labsCheckoutItemId);

  const [patientSignUp, { loading: isCreatingPatient }] =
    usePatientSignUpUnauthenticatedSharedMutation({
      onCompleted: data => {
        const signUpResult = data?.auth?.patientSignUp;

        if (signUpResult?.errors?.message) {
          makeToast("error", signUpResult.errors.message);

          setIsSubmitting(false); // Reset submitting state on error

          return;
        }

        if (signUpResult?.errors?.fields) {
          const fieldErrors = signUpResult.errors.fields;
          if (fieldErrors.password) {
            makeToast("error", fieldErrors.password[0]);

            setIsSubmitting(false); // Reset submitting state on error

            return;
          }
          // Handle other field errors if they exist
          const firstError = Object.values(fieldErrors)[0];
          if (firstError && Array.isArray(firstError) && firstError.length > 0) {
            makeToast("error", firstError[0]);

            setIsSubmitting(false); // Reset submitting state on error

            return;
          }
        }

        // Success - let backend handle the redirect
        if (signUpResult?.redirectPath) {
          window.location.assign(signUpResult.redirectPath);

          // Don't reset isSubmitting here - keep button disabled until redirect
          return;
        }

        // If no redirect path, call the success callback
        onPatientCreated?.();
        // Don't reset isSubmitting here either - let the parent handle the state
      },
      onError: error => {
        makeToast("error", error?.message || t(l.journeyCheckout.patientInfo.errors.signUpError));
        setIsSubmitting(false); // Reset submitting state on error
      },
    });

  const handleFirstNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFirstName(e.target.value);
  };

  const handleLastNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setLastName(e.target.value);
  };

  const handleDateOfBirthChange = (date: Date | undefined) => {
    setDateOfBirth(date);
  };

  const handleBiologicalSexChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setBiologicalSex(e.target.value);
  };

  const handlePhoneNumberChange = (formattedPhoneNumber: string) => {
    setPhoneNumber(formattedPhoneNumber);
  };

  const handleContinue = () => {
    if (!isFormValid) {
      return;
    }

    onSubmit?.();

    setIsSubmitting(true);

    // Clear any previous reCAPTCHA errors
    setRecaptchaError(false);

    gRecaptchaExecute(
      (captchaToken: string) => {
        const optional: AuthPatientSignUpFromRxOptionalAttributes = {
          source,
          dateOfBirth: dobISOString,
          sex: biologicalSex || undefined,
          smsNumber: phoneNumber || undefined,
        };

        if (hasLabsCheckoutAuth) {
          optional.labsCheckoutItemType = labsCheckoutItemType;
          optional.labsCheckoutItemId = labsCheckoutItemId;
        } else if (redirectPath) {
          optional.redirectPath = redirectPath;
        }

        patientSignUp({
          variables: {
            input: {
              attributes: {
                firstName,
                lastName,
                email: displayEmail,
                password,
                storeId: storeId || "",
                intake: true, // This is for patient intake
                optional,
              },
              captchaToken,
            },
          },
        });
      },
      () => {
        // reCAPTCHA error callback
        setRecaptchaError(true);
        setIsSubmitting(false); // Reset submitting state on reCAPTCHA error
        makeToast("error", t(l.journeyCheckout.patientInfo.errors.recaptchaError));
      }
    );
  };

  const phoneDigits = phoneNumber ? getPhoneWithoutCountryCode(phoneNumber) : "";
  const minPhoneLength = getMinPhoneLength("US");
  const isPhoneValid = phoneDigits && isValidPhoneNumberPerCountry(phoneDigits, minPhoneLength);

  const isDirectLabsCheckout = source === "labs_self_checkout";
  const isMinorBlockedFromJourney = !isDirectLabsCheckout && !!dateOfBirth && isMinor(dateOfBirth);

  const isFormValid =
    firstName &&
    lastName &&
    dateOfBirth &&
    isDateOfBirthValid(dateOfBirth) &&
    !isMinorBlockedFromJourney &&
    biologicalSex &&
    phoneNumber &&
    isPhoneValid &&
    password &&
    isPasswordValid(password) &&
    storeId &&
    displayEmail &&
    !recaptchaError &&
    !isPregnantOrNursing;

  const dobErrorCaption = useMemo(() => {
    if (!dateOfBirth) {
      return undefined;
    }

    if (!isDateOfBirthValid(dateOfBirth)) {
      return t(l.sharedCommon.InvalidDOB);
    }

    if (isMinorBlockedFromJourney) {
      return t(l.sharedCommon.DOBMustBeAtLeast18YearsOld);
    }

    return undefined;
  }, [dateOfBirth, isMinorBlockedFromJourney, t]);

  // PhoneInput component handles its own validation internally

  // Source-aware availability banner: this form is shared by Journey and
  // direct-lab signups, so the copy must reflect what's actually in the cart.
  const availabilityMessage = isDirectLabsCheckout
    ? l.journeyCheckout.patientInfo.directLabsAvailable
    : l.journeyCheckout.patientInfo.journeysAvailable;

  const renderSuccessMessage = () => (
    <Message isColor="success" customIcon={faCheckCircle}>
      <Typography>{t(availabilityMessage)}</Typography>
    </Message>
  );

  const renderFormFields = () => (
    <div>
      <Columns>
        <Columns.Column columnWidth={6}>
          <FloatingLabelInput
            id="firstName"
            type="text"
            label={t(l.journeyCheckout.patientInfo.firstNameLabel)}
            value={firstName}
            onChange={handleFirstNameChange}
            required
          />
        </Columns.Column>
        <Columns.Column columnWidth={6}>
          <FloatingLabelInput
            id="lastName"
            type="text"
            label={t(l.journeyCheckout.patientInfo.lastNameLabel)}
            value={lastName}
            onChange={handleLastNameChange}
            required
          />
        </Columns.Column>
      </Columns>
      <Columns>
        <Columns.Column columnWidth={6}>
          <DatePicker
            id="dateOfBirth"
            label={t(l.journeyCheckout.patientInfo.dateOfBirthLabel)}
            value={dateOfBirth}
            onChange={handleDateOfBirthChange}
            maxDate={new Date()}
            errorCaption={dobErrorCaption}
            required
          />
        </Columns.Column>
        <Columns.Column columnWidth={6}>
          <FloatingLabelSelect
            id="biologicalSex"
            label={t(l.journeyCheckout.patientInfo.biologicalSexLabel)}
            value={biologicalSex}
            handleChange={handleBiologicalSexChange}
            required
          >
            <option value="">{t(l.journeyCheckout.patientInfo.selectPlaceholder)}</option>
            <option value="female">
              {t(l.journeyCheckout.patientInfo.biologicalSexOptions.female)}
            </option>
            <option value="male">
              {t(l.journeyCheckout.patientInfo.biologicalSexOptions.male)}
            </option>
          </FloatingLabelSelect>
          <Arrange alignment="center" gap="small">
            <Tooltip
              tooltipContent={t(l.journeyCheckout.patientInfo.biologicalSexTooltipText)}
              enterDelay={0}
            >
              <FontAwesomeIcon icon={faInfoCircle} />
            </Tooltip>
            <Typography type="footnote">
              {t(l.journeyCheckout.patientInfo.biologicalSexTooltipLabel)}
            </Typography>
          </Arrange>
        </Columns.Column>
      </Columns>
      <Columns isMarginless>
        <Columns.Column columnWidth={6}>
          <PhoneInput
            id="phoneNumber"
            label={t(l.journeyCheckout.patientInfo.phoneNumberLabel)}
            value={phoneNumber}
            onChange={handlePhoneNumberChange}
            required
          />
          <Arrange alignment="center" gap="small">
            <FontAwesomeIcon icon={faInfoCircle} />
            <Typography type="footnote">
              {t(l.journeyCheckout.patientInfo.requiredForLabTesting)}
            </Typography>
          </Arrange>
        </Columns.Column>
        <Columns.Column columnWidth={6}>
          <PasswordInput
            value={password}
            onChange={e => setPassword(e.target.value)}
            label={t(l.journeyCheckout.patientInfo.passwordLabel)}
            isDirty={passwordDirty}
            isLoading={isCreatingPatient}
            onBlur={() => setPasswordDirty(true)}
            required
          />
        </Columns.Column>
      </Columns>
    </div>
  );

  const renderPregnantOrNursingCheckbox = () => {
    if (biologicalSex !== "female") {
      return null;
    }

    return (
      <div>
        <Spacer height="one" />
        <Checkbox
          checked={isPregnantOrNursing}
          onChange={() => setIsPregnantOrNursing(!isPregnantOrNursing)}
        >
          <Typography>{t(l.journeyCheckout.patientInfo.pregnantOrLactatingLabel)}</Typography>
        </Checkbox>
        {!!isPregnantOrNursing && (
          <>
            <Spacer height="oneHalf" />
            <Message isColor="info">
              <Typography>
                {t(l.journeyCheckout.patientInfo.journeysUnavailablePregnantOrLactating)}
              </Typography>
            </Message>
          </>
        )}
      </div>
    );
  };

  const renderContinueButton = () => (
    <Button
      onClick={handleContinue}
      isFullWidth
      size="large"
      isLoading={isCreatingPatient}
      disabled={!isFormValid || isCreatingPatient || isSubmitting}
    >
      {t(l.journeyCheckout.patientInfo.continueButton)}&nbsp;
      <FontAwesomeIcon icon={faChevronRight} />
    </Button>
  );

  const renderAccountMessage = () => (
    <Message isColor="info" customIcon={faHeart}>
      <Typography type="footnote">
        {t(l.journeyCheckout.patientInfo.accountWillBeCreated)}&nbsp;
        <strong>{displayEmail}</strong>.&nbsp;
        {t(l.journeyCheckout.patientInfo.accountBenefits)}
      </Typography>
    </Message>
  );

  return (
    <div>
      {!!isMobile && <Spacer height="one" />}
      {renderSuccessMessage()}
      <Spacer height="one" />
      <Typography type="h2">{t(l.journeyCheckout.patientInfo.title)}</Typography>
      <Spacer height="one" />
      {!!isDirectLabsCheckout && (
        <>
          <Message isColor="info">
            <Typography>{t(l.journeyCheckout.patientInfo.personBeingTestedMessage)}</Typography>
          </Message>
          <Spacer height="one" />
        </>
      )}
      {renderFormFields()}
      {renderPregnantOrNursingCheckbox()}
      <Spacer height="one" />
      {renderContinueButton()}
      <Spacer height="oneHalf" />
      {renderAccountMessage()}
    </div>
  );
};

export { JourneyPatientInfoForm };
